import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DashboardService } from '../../../redux/services/dashboard/dashboard.service';
import { take, tap, withLatestFrom, map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { State, getCustomerConfigState, getDashboardOptions } from '../../../redux/reducers';
import { Change, Reset } from '../../../redux/actions/dashboard/actions-options';
import { stringToNgbDate, ngbDateToString } from '../../../redux/services/utils/data-helpers';
import { dateTypeOptions, turnoverOptions, allowedDateTypes } from '../../../redux/models/dashboard/option-values';
import { Update } from '../../../redux/actions/dashboard/actions-report';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

@Component({
  standalone: false,
  selector: 'app-dashboard-filters',
  templateUrl: './dashboard-filters.component.html',
  styleUrls: ['./dashboard-filters.component.scss']
})
export class DashboardFiltersComponent implements OnInit, OnDestroy {
  @HostBinding() class = 'dashboard-filters';
  formGroup: UntypedFormGroup;

  allowedTurnovers = [];
  allowedDateTypes = dateTypeOptions;

  changeSubscription: Subscription;
  stateSubscription: Subscription;
  canShow = false;
  minDateFrom: NgbDate;
  minDateTo: NgbDate;
  dashboardOptionsDateTypeString = $localize`:@@dashboard-filters.dashboardOptionsDate:Date Type`;
  dashboardOptionsEndDateString = $localize`:@@dashboard-filters.dashboardOptionsEndDate:End Date`;
  dashboardOptionsStartDateString = $localize`:@@dashboard-filters.dashboardOptionsStartDate:Start Date`;
  dashboardOptionsTurnoverString = $localize`:@@dashboard-filters.dashboardOptionsTurnover:Segment Type`;
  dashboardOptionsTooltipPart1String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart1:Your dashboard is exclusive and gives you access to the following information for great decisions`;
  dashboardOptionsTooltipPart2String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart2:1 - Travel Seasonality: `;
  dashboardOptionsTooltipPart3String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart3:shows when your customers are going on travel (according to the first flight in PNR)`;
  dashboardOptionsTooltipPart4String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart4:2 - PNR Creation: `;
  dashboardOptionsTooltipPart5String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart5:shows when your customers were ready to book (according to PNR creation date)`;
  dashboardOptionsTooltipPart6String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart6:3 - Ticket issuance: `;
  dashboardOptionsTooltipPart7String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart7:shows when the sale is completed (according to the first ticket issued in PNR)`;
  dashboardOptionsTooltipPart8String = $localize`:@@dashboard-filters.dashboardOptionsTooltipPart8:Dashboard information is pulled from live data coming directly from your commercial activities.`;
  dashboardOptionsTooltipString = `${this.dashboardOptionsTooltipPart1String}<br><br>
  <strong>${this.dashboardOptionsTooltipPart2String}</strong><br>
  ${this.dashboardOptionsTooltipPart3String}<br><br>
  <strong>${this.dashboardOptionsTooltipPart4String}</strong><br>
  ${this.dashboardOptionsTooltipPart5String}<br><br>
  <strong>${this.dashboardOptionsTooltipPart6String}</strong><br>
  ${this.dashboardOptionsTooltipPart7String}<br><br>
  ${this.dashboardOptionsTooltipPart8String}<br><br>`;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dashboardService: DashboardService,
    private readonly store: Store<State>
  ) {}

  ngOnInit() {
    this.createFormGroup();
    this.initStartingValue();
    this.changeSubscription = this.formGroup.valueChanges.subscribe(this.valueChangeHandler.bind(this));
    this.stateSubscription = this.store.pipe(select(getDashboardOptions)).subscribe(optionsState => {
      const formValue: any = { ...optionsState.filters };
      formValue.dateFrom = stringToNgbDate(optionsState.filters.dateFrom);
      formValue.dateTo = stringToNgbDate(optionsState.filters.dateTo);
      formValue.dateType = this.getDateTypeValue();
      this.formGroup.patchValue(formValue, { emitEvent: false });
      this.updateMinDates(optionsState.filters.turnover);
    });
  }

  ngOnDestroy() {
    this.changeSubscription.unsubscribe();
    this.stateSubscription.unsubscribe();
    this.store.dispatch(new Reset());
    this.store.dispatch(new Update());
  }

  private valueChangeHandler(formValue: any) {
    if (!this.formGroup.valid) {
      return;
    }
    if (this.updateMinDates(formValue.turnover)) {
      return;
    }
    const filters = { ...this.formGroup.value };
    filters.dateFrom = ngbDateToString(filters.dateFrom);
    filters.dateTo = ngbDateToString(filters.dateTo);
    const newOptions: any = { filters };
    this.store.dispatch(new Change({ options: newOptions }));
  }

  private createFormGroup() {
    this.formGroup = this.formBuilder.group({
      turnover: ['document_issue'],
      dateType: ['segment_date'],
      dateFrom: [null, Validators.required],
      dateTo: [null, Validators.required]
    });
  }

  private initStartingValue() {
    this.dashboardService
      .getNewLoadedDashboard()
      .pipe(
        withLatestFrom(this.store.pipe(select(getCustomerConfigState))),
        tap(([dashboardState]) => {
          this.allowedTurnovers = dashboardState.config.config.booking_types
            ? turnoverOptions.filter(({ value }) => dashboardState.config.config.booking_types.indexOf(value) > -1)
            : [];
        }),
        map(([dashboardState]) => dashboardState),
        take(1)
      )
      .subscribe(dashboardState => {
        const formValue = {
          ...this.formGroup.value,
          ...dashboardState.options.filters
        };
        formValue.dateFrom = stringToNgbDate(formValue.dateFrom);
        formValue.dateTo = stringToNgbDate(formValue.dateTo);
        formValue.dateType = this.getDateTypeValue();
        let emitEvent = false;
        if (!this.allowedTurnovers.find(option => option.value === formValue.turnover)) {
          formValue.turnover = this.allowedTurnovers[0].value;
          emitEvent = true;
        }
        this.formGroup.patchValue(formValue, { emitEvent });
        this.canShow = !!dashboardState.config.config.booking_types;
      });
  }

  private getDateTypeValue(): string {
    const turnover = this.formGroup.get('turnover').value;
    const newAllowedDateTypes = allowedDateTypes[turnover]
      ? dateTypeOptions.filter(({ value }) => allowedDateTypes[turnover].indexOf(value) > -1)
      : dateTypeOptions;
    const currentValue = newAllowedDateTypes.find(({ value }) => value === this.formGroup.get('dateType').value);
    Promise.resolve().then(() => (this.allowedDateTypes = newAllowedDateTypes));

    return (currentValue && currentValue.value) || this.allowedDateTypes[0].value;
  }

  private updateMinDates(turnover: string): boolean {
    let triggersUpdate = false;
    const minValue = ['HOTEL', 'CAR'].indexOf(turnover) > -1 ? '2020-01-01' : '2017-08-01';
    const ngbValue = stringToNgbDate(minValue);
    let { dateFrom, dateTo } = this.formGroup.value;
    dateFrom = ngbValue.after(dateFrom) ? ngbValue : NgbDate.from(dateFrom);
    dateTo = ngbValue.after(dateTo) ? ngbValue : NgbDate.from(dateTo);
    if (dateFrom.after(dateTo)) {
      dateTo = dateFrom;
    }
    this.formGroup.patchValue({ dateFrom, dateTo }, { emitEvent: false });
    if (!ngbValue.equals(this.minDateFrom)) {
      this.minDateFrom = this.minDateTo = ngbValue;
      triggersUpdate = true;
    }
    if (this.minDateTo.after(dateFrom)) {
      this.minDateTo = dateFrom;
      triggersUpdate = true;
    }
    return triggersUpdate;
  }
}
